import {
  Button,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Space,
  Spin,
  message,
} from 'antd'
import { useEffect } from 'react'
import styles from './styles.module.scss'
import {
  AccountDetailRes,
  POST_RECOGNIZE_INVOICE,
} from '../../../../api/orderManage'
import CustomModal, { CustomModalProps } from '../../../../components/ModalUser'
import {
  DeleteFilled,
  PlusCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { useTypedSelector } from '../../../../redux/store'
import UploadSingleInvoice from './UploadSingleInvoice'
import { fileFile } from '../../../../modules/uploadfiles/hooks'
import moment from 'moment'
import { isResponseCodeSuccess } from '../../../../tools/util'

export type FinanicialInvoiceType = 'batchUpload' | 'manual'

export interface Props extends CustomModalProps {
  detailInfo?: AccountDetailRes
  form: FormInstance
  type: FinanicialInvoiceType
  loading?: boolean
}

// 上传发票Form样式
const invoiceFormItemLayout = {
  style: {
    width: '30%',
    marginBottom: '20px',
  },
}

export default function FinancialTicketModal(props: Props) {
  const { form, type, loading, open, ...restProps } = props
  const { expressList } = useTypedSelector((state) => state.homeData)

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        invoiceList: type === 'manual' ? [{}] : [],
      })
    }
    // eslint-disable-next-line
  }, [type, open])

  const handleRecognize = async (key: number, file?: fileFile[]) => {
    if (file) {
      message.loading('发票识别中，请耐心等待......')
      const fileUrls = file.map((item) => item.url).join(',')
      const res = await POST_RECOGNIZE_INVOICE({ fileUrls })
      if (isResponseCodeSuccess(res.code)) {
        const resData = res.data
        if (resData.every((item) => item.invoiceNum && item.invoiceTime)) {
          const formData: any[] = form.getFieldValue('invoiceList')
          form.setFieldsValue({
            invoiceList: formData.map((item, index) => {
              if (index === key) {
                return {
                  ...item,
                  ...resData[0],
                  invoiceTime: moment(Number(resData[0].invoiceTime)),
                }
              }
              return item
            }),
          })
          message.success('识别成功！')
        } else {
          message.error('识别失败！')
        }
      }
    }
  }

  return (
    <CustomModal
      title="上传发票"
      width={1000}
      okText="提交"
      bodyStyle={{ height: '500px', overflowY: 'auto' }}
      {...restProps}
      className={styles['modal']}
      open={open}
      footer={loading ? false : undefined}
    >
      <Spin
        spinning={type === 'batchUpload' && loading}
        indicator={
          <div>
            <Space style={{ color: 'orange' }}>
              <LoadingOutlined style={{ fontSize: 24 }} spin />
              发票识别中......请耐心等待
            </Space>
          </div>
        }
        wrapperClassName={styles['spin-box']}
      >
        <Form
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          preserve={false}
        >
          <Row hidden={loading}>
            <Form.Item
              {...invoiceFormItemLayout}
              label="物流公司"
              name="logisticsCompany"
              rules={[{ required: true, message: '请选择' }]}
            >
              <Select placeholder="请选择" allowClear showSearch>
                {expressList.map((item) => {
                  return (
                    <Select.Option
                      value={item.expressCompany}
                      key={item.expressCode}
                    >
                      {item.expressCompany}
                    </Select.Option>
                  )
                })}
              </Select>
            </Form.Item>
            <Form.Item
              {...invoiceFormItemLayout}
              label="物流单号"
              name="logisticsNum"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: '请输入',
                },
              ]}
            >
              <Input placeholder="请输入" maxLength={40} />
            </Form.Item>
          </Row>
          <Form.List name="invoiceList">
            {(fields, { add, remove }) => {
              return (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div
                      key={key}
                      className={`${styles['invoice-item']} ${styles['marginBottom-20']}`}
                    >
                      <div className={styles['item-left']}>
                        <span>{`发票${index + 1}`}</span>
                        <span>
                          <Button
                            hidden={index === 0}
                            type="link"
                            danger
                            onClick={() => remove(name)}
                            icon={<DeleteFilled />}
                          />
                        </span>
                      </div>
                      <div className={styles['item-right']}>
                        <Row>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            labelCol={{ span: 3 }}
                            wrapperCol={{ span: 20 }}
                            name={[name, 'fileList']}
                            label="发票附件"
                            extra={
                              <span
                                style={{ color: '#919191', fontSize: '12px' }}
                              >
                                支持jpg、png、jpeg、pdf
                              </span>
                            }
                            style={{ width: '80%' }}
                            rules={[
                              { required: true, message: '请上传发票附件' },
                            ]}
                          >
                            <UploadSingleInvoice
                              handleRecognize={(file) =>
                                handleRecognize(name, file)
                              }
                            />
                          </Form.Item>
                        </Row>
                        <Row>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'invoiceNum']}
                            label={'发票号'}
                            rules={[
                              {
                                required: true,
                                message: '请输入',
                              },
                            ]}
                          >
                            <Input placeholder="请输入"></Input>
                          </Form.Item>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'invoiceCode']}
                            label={'发票代码'}
                          >
                            <Input placeholder="请输入"></Input>
                          </Form.Item>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'invoiceTime']}
                            label={'开票日期'}
                          >
                            <DatePicker />
                          </Form.Item>
                        </Row>
                        <Row>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'totalAmountIncludingTax']}
                            label={'含税金额'}
                            rules={[
                              {
                                pattern: /^(0\.\d{1,2}|[1-9]\d*(\.\d{1,2})?)$/,
                                message: '仅支持大于零的两位小数',
                              },
                            ]}
                          >
                            <Input placeholder="请输入"></Input>
                          </Form.Item>
                        </Row>
                      </div>
                    </div>
                  ))}
                  {!loading && fields.length < 10 && (
                    <div className={styles['invoice-item']}>
                      <div className={styles['item-left']}></div>
                      <div className={styles['item-right']}>
                        <Button
                          size="middle"
                          onClick={() => add()}
                          icon={<PlusCircleOutlined />}
                        >
                          添加发票
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )
            }}
          </Form.List>
        </Form>
      </Spin>
    </CustomModal>
  )
}
import {
  Button,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Space,
  Spin,
  message,
} from 'antd'
import { useEffect } from 'react'
import styles from './styles.module.scss'
import {
  AccountDetailRes,
  POST_RECOGNIZE_INVOICE,
} from '../../../../api/orderManage'
import CustomModal, { CustomModalProps } from '../../../../components/ModalUser'
import {
  DeleteFilled,
  PlusCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { useTypedSelector } from '../../../../redux/store'
import UploadSingleInvoice from './UploadSingleInvoice'
import { fileFile } from '../../../../modules/uploadfiles/hooks'
import moment from 'moment'
import { isResponseCodeSuccess } from '../../../../tools/util'

export type FinanicialInvoiceType = 'batchUpload' | 'manual'

export interface Props extends CustomModalProps {
  detailInfo?: AccountDetailRes
  form: FormInstance
  type: FinanicialInvoiceType
  loading?: boolean
}

// 上传发票Form样式
const invoiceFormItemLayout = {
  style: {
    width: '30%',
    marginBottom: '20px',
  },
}

export default function FinancialTicketModal(props: Props) {
  const { form, type, loading, open, ...restProps } = props
  const { expressList } = useTypedSelector((state) => state.homeData)

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        invoiceList: type === 'manual' ? [{}] : [],
      })
    }
    // eslint-disable-next-line
  }, [type, open])

  const handleRecognize = async (key: number, file?: fileFile[]) => {
    if (file) {
      message.loading('发票识别中，请耐心等待......')
      const fileUrls = file.map((item) => item.url).join(',')
      const res = await POST_RECOGNIZE_INVOICE({ fileUrls })
      if (isResponseCodeSuccess(res.code)) {
        const resData = res.data
        if (resData.every((item) => item.invoiceNum && item.invoiceTime)) {
          const formData: any[] = form.getFieldValue('invoiceList')
          form.setFieldsValue({
            invoiceList: formData.map((item, index) => {
              if (index === key) {
                return {
                  ...item,
                  ...resData[0],
                  invoiceTime: moment(Number(resData[0].invoiceTime)),
                }
              }
              return item
            }),
          })
          message.success('识别成功！')
        } else {
          message.error('识别失败！')
        }
      }
    }
  }

  return (
    <CustomModal
      title="上传发票"
      width={1000}
      okText="提交"
      bodyStyle={{ height: '500px', overflowY: 'auto' }}
      {...restProps}
      className={styles['modal']}
      open={open}
      footer={loading ? false : undefined}
    >
      <Spin
        spinning={type === 'batchUpload' && loading}
        indicator={
          <div>
            <Space style={{ color: 'orange' }}>
              <LoadingOutlined style={{ fontSize: 24 }} spin />
              发票识别中......请耐心等待
            </Space>
          </div>
        }
        wrapperClassName={styles['spin-box']}
      >
        <Form
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          preserve={false}
        >
          <Row hidden={loading}>
            <Form.Item
              {...invoiceFormItemLayout}
              label="物流公司"
              name="logisticsCompany"
              rules={[{ required: true, message: '请选择' }]}
            >
              <Select placeholder="请选择" allowClear showSearch>
                {expressList.map((item) => {
                  return (
                    <Select.Option
                      value={item.expressCompany}
                      key={item.expressCode}
                    >
                      {item.expressCompany}
                    </Select.Option>
                  )
                })}
              </Select>
            </Form.Item>
            <Form.Item
              {...invoiceFormItemLayout}
              label="物流单号"
              name="logisticsNum"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: '请输入',
                },
              ]}
            >
              <Input placeholder="请输入" maxLength={40} />
            </Form.Item>
          </Row>
          <Form.List name="invoiceList">
            {(fields, { add, remove }) => {
              return (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div
                      key={key}
                      className={`${styles['invoice-item']} ${styles['marginBottom-20']}`}
                    >
                      <div className={styles['item-left']}>
                        <span>{`发票${index + 1}`}</span>
                        <span>
                          <Button
                            hidden={index === 0}
                            type="link"
                            danger
                            onClick={() => remove(name)}
                            icon={<DeleteFilled />}
                          />
                        </span>
                      </div>
                      <div className={styles['item-right']}>
                        <Row>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            labelCol={{ span: 3 }}
                            wrapperCol={{ span: 20 }}
                            name={[name, 'fileList']}
                            label="发票附件"
                            extra={
                              <span
                                style={{ color: '#919191', fontSize: '12px' }}
                              >
                                支持jpg、png、jpeg、pdf
                              </span>
                            }
                            style={{ width: '80%' }}
                            rules={[
                              { required: true, message: '请上传发票附件' },
                            ]}
                          >
                            <UploadSingleInvoice
                              handleRecognize={(file) =>
                                handleRecognize(name, file)
                              }
                            />
                          </Form.Item>
                        </Row>
                        <Row>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'invoiceNum']}
                            label={'发票号'}
                            rules={[
                              {
                                required: true,
                                message: '请输入',
                              },
                            ]}
                          >
                            <Input placeholder="请输入"></Input>
                          </Form.Item>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'invoiceCode']}
                            label={'发票代码'}
                          >
                            <Input placeholder="请输入"></Input>
                          </Form.Item>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'invoiceTime']}
                            label={'开票日期'}
                          >
                            <DatePicker />
                          </Form.Item>
                        </Row>
                        <Row>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'totalAmountIncludingTax']}
                            label={'含税金额'}
                            rules={[
                              {
                                pattern: /^(0\.\d{1,2}|[1-9]\d*(\.\d{1,2})?)$/,
                                message: '仅支持大于零的两位小数',
                              },
                            ]}
                          >
                            <Input placeholder="请输入"></Input>
                          </Form.Item>
                        </Row>
                      </div>
                    </div>
                  ))}
                  {!loading && fields.length < 10 && (
                    <div className={styles['invoice-item']}>
                      <div className={styles['item-left']}></div>
                      <div className={styles['item-right']}>
                        <Button
                          size="middle"
                          onClick={() => add()}
                          icon={<PlusCircleOutlined />}
                        >
                          添加发票
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )
            }}
          </Form.List>
        </Form>
      </Spin>
    </CustomModal>
  )
}
